import { useEffect, useRef } from 'react';
import { Spinner } from './Spinner';
import { Bot, Headphones } from 'lucide-react';
import { renderChatMarkdown } from '@lasoxp/presentation-utils/chat-markdown';

export interface ConversationMessage {
  id: string;
  sender: 'user' | 'ai' | 'agent';
  content: string;
  timestamp: Date;
  senderName?: string;
  suggestions?: string[];
}

export interface ConversationViewProps {
  messages: ConversationMessage[];
  loading?: boolean;
  className?: string;
}

function formatMessageTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function shouldShowTimestamp(current: Date, previous: Date | null): boolean {
  if (!previous) return true;
  return current.getTime() - previous.getTime() > 5 * 60 * 1000; // 5 minutes
}

function formatTimestampDivider(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Today at ${formatMessageTime(date)}`;
  }
  if (diffDays === 1) {
    return `Yesterday at ${formatMessageTime(date)}`;
  }
  return `${date.toLocaleDateString()} at ${formatMessageTime(date)}`;
}

export function ConversationView({ messages, loading, className = '' }: ConversationViewProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof bottomRef.current?.scrollIntoView === 'function') {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Spinner size="md" label="Loading conversation..." inline={false} />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className={`flex items-center justify-center py-12 text-surface-500 ${className}`}>
        <p className="text-sm">No messages yet</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 px-4 py-3 overflow-y-auto ${className}`}>
      {messages.map((msg, i) => {
        const prevTimestamp = i > 0 ? messages[i - 1].timestamp : null;
        const showTimestamp = shouldShowTimestamp(msg.timestamp, prevTimestamp);

        return (
          <div key={msg.id}>
            {showTimestamp && (
              <div className="flex justify-center my-3">
                <span className="text-[11px] text-surface-400 bg-surface-50 px-2.5 py-0.5 rounded-full">
                  {formatTimestampDivider(msg.timestamp)}
                </span>
              </div>
            )}

            {msg.sender === 'user' ? (
              /* User message — right-aligned */
              <div className="flex justify-end mb-1.5">
                <div className="max-w-[75%] flex flex-col items-end">
                  <div className="bg-primary-500 text-white px-3.5 py-2 rounded-2xl rounded-br-sm text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </div>
                  <span className="text-[10px] text-surface-400 mt-0.5 mr-1">
                    {formatMessageTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ) : msg.sender === 'agent' ? (
              /* Agent message — left-aligned, green */
              <div className="flex justify-start mb-1.5">
                <div className="flex items-start gap-2 max-w-[75%]">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Headphones className="w-3.5 h-3.5 text-green-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-green-600 font-medium mb-0.5">
                      {msg.senderName || 'Agent'}
                    </span>
                    <div
                      className="bg-green-50 text-green-800 border border-green-200 px-3.5 py-2 rounded-2xl rounded-bl-sm text-sm leading-relaxed chat-markdown"
                      dangerouslySetInnerHTML={{ __html: renderChatMarkdown(msg.content) }}
                    />
                    <span className="text-[10px] text-surface-400 mt-0.5 ml-1">
                      {formatMessageTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* AI message — left-aligned, gray */
              <div className="flex justify-start mb-1.5">
                <div className="flex items-start gap-2 max-w-[75%]">
                  <div className="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-surface-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] text-surface-500 font-medium mb-0.5">AI</span>
                    <div
                      className="bg-surface-100 text-surface-800 px-3.5 py-2 rounded-2xl rounded-bl-sm text-sm leading-relaxed chat-markdown"
                      dangerouslySetInnerHTML={{ __html: renderChatMarkdown(msg.content) }}
                    />
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {msg.suggestions.map((s, si) => (
                          <span
                            key={si}
                            className="text-[11px] text-primary-600 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded-full"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="text-[10px] text-surface-400 mt-0.5 ml-1">
                      {formatMessageTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
